import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Printer, Download } from 'lucide-react';

const JOURS_SEMAINE = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];
const JOURS_WEEKEND = ['samedi', 'dimanche'];
const HORAIRES_NORMAL = [
  { index: 0, label: '09:00-10:30' },
  { index: 1, label: '10:45-12:15' },
  { index: 2, label: '13:15-14:45' },
  { index: 3, label: '15:00-16:30' }
];
const HORAIRES_TA = [
  { index: 0, label: '19:00-20:30' },
  { index: 1, label: '20:30-22:00' },
  { index: 2, label: '09:30-11:00' },
  { index: 3, label: '11:00-12:30' },
  { index: 4, label: '13:30-15:00' },
  { index: 5, label: '15:00-16:30' }
];

const normaliserRegime = (r) => (String(r || '').toUpperCase() === 'TA' ? 'TA' : 'NORMAL');
const regimeDeClasse = (c) => normaliserRegime(c && (c.regime || c.regime_classe));

const creneauValide = (regime, jour, periode, duree) => {
  const p = parseInt(periode, 10);
  const d = parseInt(duree, 10) || 1;
  if (normaliserRegime(regime) !== 'TA') {
    return (JOURS_SEMAINE.includes(jour) || jour === 'samedi') && p >= 0 && p <= 3 && (d === 1 || (d === 2 && p <= 2));
  }
  if (d !== 1 && d !== 2) return false;
  if (JOURS_SEMAINE.includes(jour)) {
    if (p !== 0 && p !== 1) return false;
    if (d === 2 && p !== 0) return false;
    return true;
  }
  if (JOURS_WEEKEND.includes(jour)) {
    if (p < 2 || p > 5) return false;
    if (d === 2 && p !== 2 && p !== 4) return false;
    return true;
  }
  return false;
};

// Meme regle que dans EmploiTemps : la liste proposee pour une seance est celle du NIVEAU
// de la classe concernee, restreint a l annee active. Elle ne se resout plus jamais par
// « toutes les matieres », sinon le menu de modification proposait des matieres d un autre
// groupe que celui que l on est en train de modifier.
const matieresDuNiveau = (classe, matieres, modules, anneeActive) => {
  const niveauId = classe && (classe.niveau_id || classe.niveau);
  if (!niveauId) return [];
  const modulesDuNiveau = modules
    .filter(m => String(m.niveau_id) === String(niveauId))
    .filter(m => !anneeActive || String(m.annee_universitaire_id) === String(anneeActive));
  if (modulesDuNiveau.length === 0) return [];
  const ids = new Set(modulesDuNiveau.map(m => String(m._id || m.id)));
  return matieres.filter(m =>
    ids.has(String(m.module_id)) && (!anneeActive || String(m.annee_universitaire_id) === String(anneeActive)));
};

const EmploiDirection = () => {
  const [classes, setClasses] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [professeurs, setProfesseurs] = useState([]);
  const [salles, setSalles] = useState([]);
  const [cards, setCards] = useState([]);
  const [lecons, setLecons] = useState([]);
const [modules, setModules] = useState([]);
const [anneeActive, setAnneeActive] = useState('');

  const [mode, setMode] = useState('groupe');
  const [selectedGroupe, setSelectedGroupe] = useState('');
  const [selectedProf, setSelectedProf] = useState('');
  const [regimeFiltre, setRegimeFiltre] = useState('NORMAL');
  const [emploiData, setEmploiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modification et suppression d'une seance : reservees aux roles qui administrent l emploi
  // du temps. Le backend refuse aussi l operation cote serveur, ce controle evite surtout
  // d afficher des boutons qui echoueront.
  const roleUtilisateur = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role || ''; } catch (e) { return ''; }
  })();
  const peutModifier = ['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'GESTIONNAIRE', 'CHEF_FILIERE'].includes(roleUtilisateur);

  const [modifierOuvert, setModifierOuvert] = useState(false);
  const [suppressionCible, setSuppressionCible] = useState(null);
  const [actionEnCours, setActionEnCours] = useState(false);
  const [actionErreur, setActionErreur] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [formCours, setFormCours] = useState({ matiereId: '', professeurId: '', salleId: '', jour: '', periode: 0, duree: 1 });

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // En mode groupe, le regime est celui de la classe. En mode enseignant, l utilisateur
  // choisit l emploi a consulter : un meme prof peut intervenir en normal et en TA.
  const classeGroupe = classes.find(c => c.name === selectedGroupe);
  const regime = mode === 'groupe' ? regimeDeClasse(classeGroupe) : regimeFiltre;
  const estTA = regime === 'TA';
  const jours = estTA ? [...JOURS_SEMAINE, ...JOURS_WEEKEND] : [...JOURS_SEMAINE, 'samedi'];
  const joursLabels = jours.map(j => j.charAt(0).toUpperCase() + j.slice(1));
  const horaires = estTA ? HORAIRES_TA : HORAIRES_NORMAL;
  const horairesFormulaire = !estTA
    ? HORAIRES_NORMAL
    : (JOURS_WEEKEND.includes(formCours.jour)
      ? HORAIRES_TA.filter(h => h.index >= 2)
      : HORAIRES_TA.filter(h => h.index <= 1));

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if ((mode === 'groupe' && selectedGroupe && lecons.length > 0) ||
        (mode === 'prof' && selectedProf && lecons.length > 0)) {
      generateEmploi();
    }
  }, [mode, selectedGroupe, selectedProf, regimeFiltre, classes, lecons, cards, matieres, professeurs, salles]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [classesRes, matieresRes, profsRes, sallesRes, cardsRes, leconsRes, modulesRes, anneesRes] = await Promise.all([
        axios.get('/api/emploi/classes', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/matieres', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/professeurs', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/salles', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/cards', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/lecons', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/modules', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/annees', { headers, timeout: 8000 }).catch(() => ({ data: [] }))
      ]);

      setClasses(classesRes.data || []);
      setMatieres((matieresRes.data || []).map(m => ({ ...m, id: m.id || m._id, name: m.name || m.nom })));
      setProfesseurs(profsRes.data || []);
      setSalles(sallesRes.data || []);
      setCards(cardsRes.data || []);
      setLecons(leconsRes.data || []);
      setModules(modulesRes.data || []);
      const anneeActive = (anneesRes.data || []).find(a => a.estActive);
      setAnneeActive(anneeActive ? String(anneeActive.annee_id || anneeActive._id) : '');

      if (classesRes.data && classesRes.data.length > 0) {
        setSelectedGroupe(classesRes.data[0].name);
      }
      if (profsRes.data && profsRes.data.length > 0) {
        setSelectedProf(profsRes.data[0].id || profsRes.data[0]._id);
      }
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données de l\'emploi du temps');
    } finally {
      setLoading(false);
    }
  };

  const getClassNames = (classIds) => {
    if (!classIds || classIds.length === 0) return '';
    return classIds
      .map(id => {
        const c = classes.find(cl => (cl.id || cl._id) === id);
        return c ? c.name : '';
      })
      .filter(Boolean)
      .join(', ');
  };

  const generateEmploi = () => {
    const entries = [];
    const regimeCourant = mode === 'groupe' ? regimeDeClasse(classeGroupe) : normaliserRegime(regimeFiltre);
    const classe = classes.find(c => c.name === selectedGroupe);

    for (const card of cards) {
      const lecon = lecons.find(l => l.id === card.lessonId);
      if (!lecon) continue;
      if (normaliserRegime(card.regime || lecon.regime) !== regimeCourant) continue;

      if (mode === 'groupe') {
        if (!classe || !(lecon.classIds || []).some(x => String(x) === String(classe.id || classe._id))) continue;
      } else {
        if (lecon.teacherId !== selectedProf) continue;
      }

      const matiere = matieres.find(m => m.id === lecon.subjectId);
      const professeur = professeurs.find(p => (p.id || p._id) === lecon.teacherId);
      const salle = salles.find(s => s.id === (card.classroomId || lecon.classroomId));

      const daysBit = parseInt(card.days) || 0;
      const joursCours = [];
      if (daysBit & 1) joursCours.push('lundi');
      if (daysBit & 2) joursCours.push('mardi');
      if (daysBit & 4) joursCours.push('mercredi');
      if (daysBit & 8) joursCours.push('jeudi');
      if (daysBit & 16) joursCours.push('vendredi');
      if (daysBit & 32) joursCours.push('samedi');
      if (daysBit & 64) joursCours.push('dimanche');

      const periode = parseInt(card.period);
      const duree = parseInt(card.duree) || 1;
      if (!horaires[periode]) continue;

      // Nombre de creneaux du meme cours : un cours peut occuper plusieurs cellules, la
      // suppression doit le signaler pour que la direction ne supprime pas un seul creneau par erreur.
      const nbCreneaux = cards.filter(c => c.lessonId === card.lessonId).length;

      for (const jour of joursCours) {
        if (!creneauValide(regimeCourant, jour, periode, duree)) continue;
        entries.push({
          id: card.lessonId,
          cardId: card._id,
          jour: jour,
          periode: periode,
          duree: duree,
          regime: regimeCourant,
          matiere: matiere?.name || lecon.subjectName || 'Sans matière',
          professeur: professeur?.name || lecon.teacherName || 'Sans professeur',
          salle: salle?.name || card.classroomName || 'Salle inconnue',
          groupes: getClassNames(lecon.classIds),
          // Identifiants conserves pour pre-remplir la modification : sans eux, la boite de
          // dialogue ne saurait pas quoi mettre a jour.
          subjectId: lecon.subjectId,
          teacherId: lecon.teacherId,
          classroomId: card.classroomId || lecon.classroomId,
          classIds: lecon.classIds || [],
          nbCreneaux
        });
      }
    }

    setEmploiData(entries);
  };

  const getCourses = (jour, periode) => {
    return emploiData.filter(c => c.jour === jour && c.periode === periode);
  };

  const getSpanContinuing = (jour, periode) => {
    return emploiData.find(c => c.jour === jour && c.periode === periode - 1 && c.duree === 2);
  };

  const getTypeCours = (matiere) => {
    if (!matiere) return { bg: '#f3f4f6', color: '#6b7280', label: 'Cours', short: 'CM' };
    const nom = matiere.toLowerCase();
    if (nom.includes('tp') || nom.includes('pratique')) return { bg: '#fef3c7', color: '#92400e', label: 'Travaux Pratiques (TP)', short: 'TP' };
    if (nom.includes('td') || nom.includes('dirigé')) return { bg: '#dcfce7', color: '#166534', label: 'Travaux Dirigés (TD)', short: 'TD' };
    return { bg: '#dbeafe', color: '#1e40af', label: 'Cours Magistral (CM)', short: 'CM' };
  };

  const getTitre = () => {
    const suffixe = estTA ? ' (TA)' : '';
    if (mode === 'groupe') return `Emploi du temps - Groupe ${selectedGroupe}${suffixe}`;
    const prof = professeurs.find(p => (p.id || p._id) === selectedProf);
    return `Emploi du temps - ${prof?.name || selectedProf}${suffixe}`;
  };

  const handlePrint = () => {
    if (mode === 'groupe' && !selectedGroupe) return;
    if (mode === 'prof' && !selectedProf) return;

    const printWindow = window.open('', '_blank');
    const esc = (s = '') => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const titre = getTitre();

    let tableRows = '';
    for (const jour of jours) {
      let row = `<tr>
        <td class="jour-col">${jour.charAt(0).toUpperCase() + jour.slice(1)}</td>`;
      for (const horaire of horaires) {
        const spanCont = getSpanContinuing(jour, horaire.index);
        if (spanCont) continue;
        const coursList = getCourses(jour, horaire.index);
        if (coursList.length > 0) {
          const cell = coursList.map(c => {
            const typeStyle = getTypeCours(c.matiere);
            return `
              <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; height: 100%; margin-bottom: ${coursList.length > 1 ? '8px' : '0'};">
                <div class="cours-title" style="color: ${typeStyle.color};">${esc(c.matiere)}</div>
                <div class="cours-info">📍 ${esc(c.salle)} · 👨‍🏫 ${esc(c.professeur)}</div>
                ${mode === 'prof' && c.groupes ? `<div class="cours-info">🎓 ${esc(c.groupes)}</div>` : ''}
              </div>`;
          }).join('');
          const colspan = coursList.length === 1 && coursList[0].duree === 2 ? ' colspan="2"' : '';
          const cellBg = coursList.length === 1 && coursList[0].duree === 2 ? getTypeCours(coursList[0].matiere).bg : 'transparent';
          row += `<td class="cours-cell"${colspan} style="background: ${cellBg};">${cell}</td>`;
        } else {
          row += `<td class="cours-cell" style="text-align: center; color: #9ca3af;">-</td>`;
        }
      }
      row += `</tr>`;
      tableRows += row;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${esc(titre)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #333; }
          .header { display: flex; align-items: center; gap: 18px; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #3b82f6; }
          .header .logo { width: 72px; height: 72px; object-fit: contain; flex: 0 0 auto; }
          .header-titres { flex: 1; text-align: center; }
          .header h1 { color: #1f2937; font-size: 24px; margin-bottom: 5px; }
          .header h2 { color: #3b82f6; font-size: 18px; margin-bottom: 0; }
          .classe-info { text-align: center; margin-bottom: 20px; padding: 10px; background: #f3f4f6; border-radius: 8px; }
          .classe-info h3 { color: #1f2937; font-size: 16px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #3b82f6; color: white; padding: 12px; text-align: center; font-weight: bold; }
          td { border: 1px solid #e5e7eb; padding: 10px; vertical-align: top; }
          .heure-col { background: #f3f4f6; font-weight: bold; text-align: center; width: 100px; }
          .jour-col { background: #f3f4f6; font-weight: bold; text-align: center; text-transform: capitalize; width: 100px; }
          .cours-cell { min-height: 80px; }
          .cours-title { font-weight: bold; font-size: 14px; margin-bottom: 5px; }
          .cours-info { font-size: 11px; color: #6b7280; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 20px; }
          @media print {
            body { padding: 0; }
            th { background: #3b82f6 !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .heure-col { background: #f3f4f6 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="/images/logo_sup2i.png" class="logo" alt="SUP2I" onerror="this.style.display='none'" />
          <div class="header-titres">
            <h1>📅 SUP2I - Emploi du Temps</h1>
            <h2>Année Universitaire 2026/2027${estTA ? ' - Temps aménagé' : ''}</h2>
          </div>
        </div>
        <div class="classe-info"><h3>🎓 ${esc(titre)}</h3></div>
        <table>
          <thead>
            <tr>
              <th>Jour</th>
              ${horaires.map(h => `<th>${h.label}</th>`).join('')}
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setEmploiData([]);
  };

  const ouvrirModification = (cours) => {
    setActionErreur('');
    setFormCours({
      matiereId: cours.subjectId || '',
      professeurId: cours.teacherId || '',
      salleId: cours.classroomId || '',
      jour: cours.jour,
      periode: cours.periode,
      duree: cours.duree || 1
    });
    setSuppressionCible(null);
    setModifierOuvert(cours);
  };

  const enregistrerModification = async () => {
    const cours = modifierOuvert;
    const periode = parseInt(formCours.periode, 10);
    const duree = parseInt(formCours.duree, 10);
    if (!creneauValide(regime, formCours.jour, periode, duree)) {
      setActionErreur(estTA
        ? 'Créneau TA invalide : soirée en semaine (19:00-22:00) ou week-end (09:30-16:30).'
        : 'Créneau invalide.');
      return;
    }
    try {
      setActionEnCours(true);
      setActionErreur('');
      await axios.put('/api/emploi/cours', {
        leconId: cours.id,
        cardId: cours.cardId,
        matiereId: formCours.matiereId,
        professeurId: formCours.professeurId,
        salleId: formCours.salleId,
        jour: formCours.jour,
        periode,
        duree,
        regime
      }, { headers: getAuthHeaders() });
      setModifierOuvert(null);
      setActionMessage('Séance modifiée');
      await loadAllData();
    } catch (err) {
      setActionErreur(err.response?.data?.error || 'Erreur lors de la modification');
    } finally {
      setActionEnCours(false);
    }
  };

  const confirmerSuppression = async () => {
    const cours = suppressionCible;
    try {
      setActionEnCours(true);
      setActionErreur('');
      await axios.delete('/api/emploi/cours', {
        headers: getAuthHeaders(),
        data: { cardId: cours.id }
      });
      setSuppressionCible(null);
      setActionMessage('Séance supprimée');
      await loadAllData();
    } catch (err) {
      setActionErreur(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setActionEnCours(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div className="loading-spinner"></div>
        <p>Chargement de l'emploi du temps...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p style={{ color: '#ef4444' }}>{error}</p>
        <button onClick={loadAllData} style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #f3e8ff 100%)',
      padding: '1.5rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>🎯 Emploi du Temps — Consultation</h1>
            <p style={{ color: '#6b7280' }}>Année Universitaire 2026/2027 · Direction</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={handlePrint} style={{ padding: '0.75rem 1.5rem', background: '#10b981', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Printer size={18} /> Imprimer
            </button>
            <button onClick={handlePrint} style={{ padding: '0.75rem 1.5rem', background: '#3b82f6', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Download size={18} /> Exporter PDF
            </button>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '16px', padding: '1rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <button onClick={() => switchMode('groupe')} style={{
                  flex: 1,
                  padding: '0.625rem',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  background: mode === 'groupe' ? '#3b82f6' : '#f3f4f6',
                  color: mode === 'groupe' ? 'white' : '#4b5563'
                }}>
                  🎓 Par Groupe
                </button>
                <button onClick={() => switchMode('prof')} style={{
                  flex: 1,
                  padding: '0.625rem',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  background: mode === 'prof' ? '#8b5cf6' : '#f3f4f6',
                  color: mode === 'prof' ? 'white' : '#4b5563'
                }}>
                  👨‍🏫 Par Enseignant
                </button>
              </div>

              {mode === 'groupe' ? (
                <>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>🎓 Groupe / Classe</label>
                  <select value={selectedGroupe} onChange={(e) => setSelectedGroupe(e.target.value)} style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {classes.map(c => <option key={c.id} value={c.name}>{c.name}{regimeDeClasse(c) === 'TA' ? ' · TA' : ''}</option>)}
                  </select>
                  {estTA && (
                    <p style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.5rem' }}>
                      Temps aménagé : soirée en semaine (19:00-22:00) et week-end (09:30-16:30).
                    </p>
                  )}
                </>
              ) : (
                <>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>👨‍🏫 Enseignant</label>
                  <select value={selectedProf} onChange={(e) => setSelectedProf(e.target.value)} style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    {professeurs.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>)}
                  </select>
                  <label style={{ display: 'block', margin: '0.75rem 0 0.5rem', fontWeight: '500' }}>🗓 Emploi</label>
                  <select value={regimeFiltre} onChange={(e) => setRegimeFiltre(e.target.value)} style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <option value="NORMAL">Normal</option>
                    <option value="TA">Temps aménagé (TA)</option>
                  </select>
                </>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.5rem', padding: '0.75rem 1rem', background: 'white', borderRadius: '12px', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '16px', height: '16px', background: '#dbeafe', borderRadius: '4px' }}></div><span>Cours Magistral (CM)</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '16px', height: '16px', background: '#dcfce7', borderRadius: '4px' }}></div><span>Travaux Dirigés (TD)</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '16px', height: '16px', background: '#fef3c7', borderRadius: '4px' }}></div><span>Travaux Pratiques (TP)</span></div>
        </div>

        <div style={{ background: 'white', borderRadius: '20px', overflowX: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '1rem', textAlign: 'center', width: '140px' }}>Jour</th>
                {horaires.map(h => <th key={h.index} style={{ padding: '1rem', textAlign: 'center', minWidth: '220px' }}>{h.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {jours.map(jour => (
                <tr key={jour} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', background: '#f9fafb', borderRight: '1px solid #e5e7eb', textTransform: 'capitalize' }}>{jour}</td>
                  {horaires.map(horaire => {
                    const coursListe = getCourses(jour, horaire.index);
                    if (getSpanContinuing(jour, horaire.index)) return null;
                    const fused = coursListe.length === 1 && coursListe[0].duree === 2;
                    const fusedStyle = fused ? getTypeCours(coursListe[0].matiere) : null;
                    return (
                      <td key={horaire.index} colSpan={fused ? 2 : undefined} style={{ padding: fused ? '0.5rem 0.75rem' : '0.75rem', verticalAlign: 'top', background: fused ? fusedStyle.bg : 'transparent' }}>
                        {coursListe.length > 0 ? coursListe.map((cours, i2) => {
                          const typeStyle = getTypeCours(cours.matiere);
                          const singleFused = cours.duree === 2 && coursListe.length === 1;
                          return (
                            <div key={i2} style={{ minHeight: '72px', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.25rem', padding: singleFused ? '0.9rem 1rem' : '0.9rem', background: singleFused ? 'transparent' : typeStyle.bg, borderRadius: singleFused ? 0 : '10px', marginBottom: coursListe.length > 1 ? '0.5rem' : 0 }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', background: typeStyle.color, color: '#fff', fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 8px', borderRadius: '99px' }}>{typeStyle.short}</div>
                              <div style={{ fontSize: singleFused ? '1.05rem' : '0.95rem', fontWeight: '700', color: typeStyle.color, lineHeight: '1.25', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cours.matiere}</div>
                              <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.1rem' }}>📍 {cours.salle} · 👨‍🏫 {cours.professeur}</div>
                              {cours.duree === 2 && (
                                <div style={{ fontSize: '0.65rem', fontWeight: '600', color: typeStyle.color, marginTop: '0.1rem' }}>⏱ Séances fusionnées — 3h</div>
                              )}
                              {mode === 'prof' && cours.groupes && (
                                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.1rem' }}>🎓 {cours.groupes}</div>
                              )}
                              {peutModifier && mode === 'prof' && (
                                <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.45rem' }}>
                                  <button
                                    onClick={() => ouvrirModification(cours)}
                                    title="Modifier cette séance"
                                    style={{ padding: '0.2rem 0.55rem', fontSize: '0.68rem', background: '#fff', border: `1px solid ${typeStyle.color}`, color: typeStyle.color, borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                                  >
                                    ✏️ Modifier
                                  </button>
                                  <button
                                    onClick={() => { setActionErreur(''); setSuppressionCible(cours); }}
                                    title="Supprimer cette séance"
                                    style={{ padding: '0.2rem 0.55rem', fontSize: '0.68rem', background: '#fff', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                                  >
                                    🗑 Supprimer
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        }) : (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '10px', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>-</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'white', borderRadius: '12px', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
          <p>📊 {emploiData.length} séance(s) programmée(s) · {getTitre()}</p>
          {mode === 'prof' && peutModifier && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#9ca3af' }}>
              Survolez une séance pour la modifier ou la supprimer. Une séance déjà réalisée ou déclarée est protégée.
            </p>
          )}
        </div>

        {actionMessage && (
          <div style={{ position: 'fixed', top: '1.5rem', right: '1.5rem', background: '#10b981', color: 'white', padding: '0.85rem 1.25rem', borderRadius: '12px', boxShadow: '0 8px 20px rgba(16,185,129,0.3)', zIndex: 2000, fontWeight: '600' }}>
            ✓ {actionMessage}
          </div>
        )}
        {actionErreur && (
          <div style={{ position: 'fixed', top: '1.5rem', right: '1.5rem', maxWidth: '520px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', padding: '0.85rem 1.25rem', borderRadius: '12px', boxShadow: '0 8px 20px rgba(0,0,0,0.12)', zIndex: 2000, fontWeight: '500' }}>
            ⚠ {actionErreur}
          </div>
        )}

        {modifierOuvert && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100, padding: '1rem' }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '1.75rem', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 45px rgba(0,0,0,0.28)' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1f2937' }}>Modifier la séance</h3>
              <p style={{ margin: '0.35rem 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
                {getTitre()} · {joursLabels[jours.indexOf(modifierOuvert.jour)]} {horaires[modifierOuvert.periode]?.label}
              </p>

              <div style={{ display: 'grid', gap: '0.85rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>Matière</label>
                  {(() => {
                    const classeSeance = classes.find(c => (cours.classIds || []).some(x => String(x) === String(c.id) || String(c._id) === String(x)));
                    const duNiveau = matieresDuNiveau(classeSeance, matieres, modules, anneeActive);
                    // La matiere deja portee par la seance reste selectionnable meme si elle
                    // n appartient plus au niveau : la retirer ferait disparaitre la valeur
                    // affichee sans l avoir demandee.
                    const courante = formCours.matiereId
                      ? matieres.filter(m => String(m.id) === String(formCours.matiereId))
                      : [];
                    const liste = [...duNiveau];
                    courante.forEach(m => { if (!liste.some(x => String(x.id) === String(m.id))) liste.unshift(m); });
                    const horsNiveau = courante.length > 0 && !duNiveau.some(m => String(m.id) === String(formCours.matiereId));
                    return (
                      <>
                        <select value={formCours.matiereId} onChange={(e) => setFormCours({ ...formCours, matiereId: e.target.value })} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '8px' }}>
                          <option value="">— Sans matière —</option>
                          {liste.map(m => <option key={m.id} value={m.id}>{m.code ? `${m.code} — ${m.name}` : m.name}</option>)}
                        </select>
                        <div style={{ fontSize: '0.7rem', color: horsNiveau ? '#b45309' : '#6b7280', marginTop: '0.25rem' }}>
                          {classeSeance
                            ? `${duNiveau.length} matière(s) du niveau ${classeSeance.niveau_id}${anneeActive ? ` — année ${anneeActive}` : ''}${horsNiveau ? ' · la matière actuelle n\'appartient pas à ce niveau' : ''}`
                            : 'Aucune classe rattachée à cette séance'}
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>Enseignant</label>
                  <select value={formCours.professeurId} onChange={(e) => setFormCours({ ...formCours, professeurId: e.target.value })} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '8px' }}>
                    <option value="">— Sans enseignant —</option>
                    {professeurs.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>Salle</label>
                  <select value={formCours.salleId} onChange={(e) => setFormCours({ ...formCours, salleId: e.target.value })} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '8px' }}>
                    <option value="">— Sans salle —</option>
                    {salles.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>Jour</label>
                    <select
                      value={formCours.jour}
                      onChange={(e) => {
                        const jour = e.target.value;
                        const dispo = !estTA
                          ? HORAIRES_NORMAL
                          : (JOURS_WEEKEND.includes(jour)
                            ? HORAIRES_TA.filter(h => h.index >= 2)
                            : HORAIRES_TA.filter(h => h.index <= 1));
                        const periode = dispo.some(h => h.index === parseInt(formCours.periode, 10))
                          ? parseInt(formCours.periode, 10)
                          : dispo[0].index;
                        const duree = creneauValide(regime, jour, periode, formCours.duree) ? formCours.duree : 1;
                        setFormCours({ ...formCours, jour, periode, duree });
                      }}
                      style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '8px' }}
                    >
                      {jours.map(j => <option key={j} value={j}>{j.charAt(0).toUpperCase() + j.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>Créneau</label>
                    <select
                      value={formCours.periode}
                      onChange={(e) => {
                        const periode = e.target.value;
                        const duree = creneauValide(regime, formCours.jour, periode, formCours.duree) ? formCours.duree : 1;
                        setFormCours({ ...formCours, periode, duree });
                      }}
                      style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '8px' }}
                    >
                      {horairesFormulaire.map(h => <option key={h.index} value={h.index}>{h.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>Durée</label>
                    <select value={formCours.duree} onChange={(e) => setFormCours({ ...formCours, duree: e.target.value })} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '8px' }}>
                      <option value={1}>1h30</option>
                      <option value={2} disabled={!creneauValide(regime, formCours.jour, formCours.periode, 2)}>3h</option>
                    </select>
                    {estTA && <p style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>3h : 19:00, 09:30 ou 13:30.</p>}
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280', background: '#f9fafb', padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                  Les classes ne sont pas modifiables ici : changer les groupes d’une séance
                  passe par l’organisation de l’emploi du temps des classes.
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.5rem' }}>
                <button onClick={() => setModifierOuvert(null)} disabled={actionEnCours} style={{ padding: '0.6rem 1.2rem', background: '#f3f4f6', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', color: '#4b5563' }}>
                  Annuler
                </button>
                <button onClick={enregistrerModification} disabled={actionEnCours} style={{ padding: '0.6rem 1.2rem', background: actionEnCours ? '#93c5fd' : '#3b82f6', border: 'none', borderRadius: '10px', color: 'white', cursor: actionEnCours ? 'default' : 'pointer', fontWeight: '600' }}>
                  {actionEnCours ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {suppressionCible && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100, padding: '1rem' }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '1.75rem', width: '100%', maxWidth: '460px', boxShadow: '0 20px 45px rgba(0,0,0,0.28)' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#991b1b' }}>Supprimer cette séance ?</h3>
              <div style={{ margin: '1rem 0', padding: '0.9rem', background: '#fef2f2', borderRadius: '10px', fontSize: '0.875rem', color: '#7f1d1d' }}>
                <div><strong>{suppressionCible.matiere}</strong> — {joursLabels[jours.indexOf(suppressionCible.jour)]} {horaires[suppressionCible.periode]?.label}</div>
                <div>📍 {suppressionCible.salle} · 👨‍🏫 {suppressionCible.professeur}</div>
                {suppressionCible.groupes && <div>🎓 {suppressionCible.groupes}</div>}
              </div>
              <p style={{ fontSize: '0.83rem', color: '#6b7280', margin: 0 }}>
                {suppressionCible.nbCreneaux > 1
                  ? `Cette séance fait partie d’un cours occupant ${suppressionCible.nbCreneaux} créneaux. Les ${suppressionCible.nbCreneaux} créneaux du cours seront retirés.`
                  : 'Le créneau sera retiré de l’emploi du temps.'}
              </p>
              <p style={{ fontSize: '0.83rem', color: '#6b7280', margin: '0.5rem 0 0' }}>
                Une séance déjà réalisée ou déclarée ne peut pas être supprimée : l’historique de présence est conservé.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.5rem' }}>
                <button onClick={() => setSuppressionCible(null)} disabled={actionEnCours} style={{ padding: '0.6rem 1.2rem', background: '#f3f4f6', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', color: '#4b5563' }}>
                  Annuler
                </button>
                <button onClick={confirmerSuppression} disabled={actionEnCours} style={{ padding: '0.6rem 1.2rem', background: actionEnCours ? '#fca5a5' : '#dc2626', border: 'none', borderRadius: '10px', color: 'white', cursor: actionEnCours ? 'default' : 'pointer', fontWeight: '600' }}>
                  {actionEnCours ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmploiDirection;